// The money block inside the ledger's claim detail panel: the two posting buttons,
// the claim's manual postings, and reversal.
//
// Self-contained on purpose. ClaimsLedgerPage.js is shared with another strand this
// sprint and the agreed change there is a mount, not a feature — so everything this
// needs (its own fetch, its own modals, its own state) lives here and the ledger
// passes a claim and a practice id.
//
// Only rendered when `features.writeoffs` is true; the ledger decides that, because
// it already has the features map.

import React, { useCallback, useEffect, useState } from "react";
import "./PostingModals.css";
import WriteoffModal from "./WriteoffModal";
import PatientPaymentModal from "./PatientPaymentModal";
import { formatCurrency } from "../../utils/formatCurrency";

const API_URL = process.env.REACT_APP_API_URL || "";

const TYPE_LABEL = {
  manual_writeoff: "Write-off",
  patient_payment: "Patient payment",
};

// `formatCurrency` renders a negative as "$-50.00", which reads as a dollar sign
// followed by nonsense. A reversal IS negative by construction, so it is the common
// case here, not an edge one.
function signedCurrency(amount) {
  const n = Number(amount) || 0;
  return n < 0 ? `-${formatCurrency(Math.abs(n))}` : formatCurrency(n);
}

function describe(posting) {
  const detail = posting.detail || {};
  if (detail.kind === "reversal") {
    return `Reversal of posting #${detail.reverses}`;
  }
  const base = TYPE_LABEL[posting.posting_type] || posting.posting_type;
  return detail.reason ? `${base} — ${detail.reason.replace(/_/g, " ")}` : base;
}

// "patient owes $10.00 of $20.00" — the total says how much of the patient's share is
// left, so a half write-off does not read as a whole one (staging walk 9b). A negative figure
// is a credit, as the statement prints it; a zero is "nothing".
export function patientPart(owed, responsibility) {
  const n = Number(owed) || 0;
  const total = Number(responsibility) || 0;
  if (n <= -0.005) {
    // A credit, said as what it is: money the patient paid beyond their share. "patient
    // credit $10.00" beside "Open balance $379.00" read as a contradiction (CDE demo,
    // 2026-10-21); next to the paid breakdown, this explains itself.
    return total >= 0.005
      ? `patient paid ${formatCurrency(Math.abs(n))} over their ${formatCurrency(total)} share`
      : `patient paid ${formatCurrency(Math.abs(n))} with no patient share set`;
  }
  const of = total >= 0.005 ? ` of ${formatCurrency(total)}` : "";
  return n >= 0.005 ? `patient owes ${formatCurrency(n)}${of}` : `patient owes nothing${of}`;
}

// "$0.00", not formatCurrency's "-": in "insurance paid -" the dash reads as unknown,
// and the whole point of the breakdown is that the figure IS known.
function paidFigure(amount) {
  const n = Number(amount) || 0;
  return Math.abs(n) < 0.005 ? "$0.00" : formatCurrency(n);
}

export default function ClaimPostings({
  practiceId,
  claim,
  readOnly = false,
  onMoneyChanged,
}) {
  const [money, setMoney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shortfall, setShortfall] = useState(null);
  // Survives a reload, unlike `error`. See applyResponse.
  const [notice, setNotice] = useState(null);
  const [showWriteoff, setShowWriteoff] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [reversingId, setReversingId] = useState(null);

  const load = useCallback(async () => {
    if (!practiceId || !claim?.id) return;
    const claimId = claim.id;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claim.id}/postings`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(
          body.error || `Could not load the postings (${resp.status})`,
        );
      }
      const body = await resp.json().catch(() => null);
      // A 200 is not a payload. A truncated body or a proxy's HTML error page
      // parses to null or to something without `postings`, and this panel would
      // then render "No write-offs or manual payments on this claim" — an
      // all-clear, produced at exactly the moment we could not check. Refuse
      // instead.
      if (!body || !Array.isArray(body.postings)) {
        throw new Error(
          "The postings for this claim could not be read. Do not assume there are none.",
        );
      }
      // C's second pattern: key staleness to the ENTITY, not to the effect. This
      // runs from `applyResponse` too, outside any effect, so an effect-scoped
      // cancelled flag would not cover it — and a late response would paint one
      // claim's money under another.
      if (body.claim_id !== undefined && body.claim_id !== claimId) return;
      setMoney(body);
    } catch (err) {
      setError(err.message);
      setMoney(null);
    } finally {
      setLoading(false);
    }
  }, [practiceId, claim?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const applyResponse = (body) => {
    if (body.money_unavailable) {
      // The payment IS recorded — the route answers 201 with the receipt even when
      // the money map could not be rebuilt afterwards. Blanking the panel would
      // read as "it did not happen" and invite the biller to enter it a second
      // time, and nothing on that path is idempotent.
      //
      // This goes in `notice`, NOT in `error`: `load()` below clears `error` as its
      // third statement, synchronously, in the same React batch — so the one
      // instruction this branch exists to deliver was being erased before it could
      // render. `notice` is cleared only when the biller dismisses it.
      setNotice(
        `Payment #${body.payment?.id ?? ""} was recorded. The balance below could ` +
          "not be refreshed — reopen this claim to see it. Do not enter it again.",
      );
      load();
    } else {
      setMoney(body);
    }
    setShortfall(body.posting?.message || null);
    if (onMoneyChanged) onMoneyChanged(body);
  };

  const reverse = async (posting) => {
    const reason = window.prompt(
      "Why is this posting being reversed? The reason is recorded on the ledger.",
    );
    if (reason === null) return;
    if (!reason.trim()) {
      setError("A reason is required to reverse a posting.");
      return;
    }
    setReversingId(posting.id);
    setError(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claim.id}/postings/${posting.id}/reverse`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason }),
        },
      );
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(
          body.error || `Could not reverse the posting (${resp.status})`,
        );
      }
      setMoney(body);
      setShortfall(null);
      if (onMoneyChanged) onMoneyChanged(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setReversingId(null);
    }
  };

  const postings = money?.postings || [];
  // `postable: false` means the ledger has no charge lines it can post against for
  // this claim (staging walk 2026-09-13): a write-off is refused, and a patient
  // payment is RECORDED but never reaches the ledger balance — so both stay disabled.
  // Also disabled until the money has loaded (or when it failed to), so neither path
  // is reachable before the panel knows.
  const notPostable = money?.postable === false;
  const postingsDisabled = money?.postable !== true;

  return (
    <div className="claim-postings">
      <h4>Manual postings</h4>

      {money && (
        <div className="posting-claim-summary">
          <span>
            Open balance <strong>{formatCurrency(money.open_balance)}</strong>
            {/* Whose it is. "Open $10 · Written off $10" read as a fully written-off
                claim when the patient had owed $20 (staging walk 9b); the patient's part,
                by the statement's own rule, makes the remainder explain itself. */}
            {typeof money.patient_open_balance === "number" && (
              <span>
                {" "}
                ({patientPart(money.patient_open_balance, money.money?.patient_responsibility)})
              </span>
            )}
          </span>
          {/* Where the money came from, from the same ledger figures as the balance.
              "Open balance $379.00 (patient credit $10.00)" told a biller nothing about
              the $50 the claim row showed as PAID; this does. */}
          {money.money && (
            <span>
              Insurance paid <strong>{paidFigure(money.money.insurance_paid)}</strong>
            </span>
          )}
          {money.money && (
            <span>
              Patient paid <strong>{paidFigure(money.money.patient_payment)}</strong>
            </span>
          )}
          {money.writeoff_total > 0 && (
            <span>
              Written off{" "}
              <strong>{formatCurrency(money.writeoff_total)}</strong>
            </span>
          )}
        </div>
      )}

      {/* The shortfall message from the server, verbatim. A write-off that posted
          less than was asked for is the one thing about this surface a biller must
          not miss, so it is shown here rather than in a toast that scrolls away. */}
      {shortfall && (
        <div className="claim-postings-shortfall">
          {shortfall}
          <button
            className="posting-modal-close"
            style={{ float: "right" }}
            onClick={() => setShortfall(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {notice && (
        <div className="claim-postings-shortfall">
          {notice}
          <button
            className="posting-modal-close"
            style={{ float: "right" }}
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {error && <div className="posting-error">{error}</div>}

      {loading && !money && <p className="claim-postings-empty">Loading…</p>}

      {money && postings.length === 0 && (
        <p className="claim-postings-empty">
          No write-offs or manual payments on this claim.
        </p>
      )}

      {postings.length > 0 && (
        <table className="claim-postings-table">
          <thead>
            <tr>
              <th>Posting</th>
              <th>By</th>
              <th>When</th>
              <th className="num">Amount</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {postings.map((p) => {
              const isReversal = p.detail?.kind === "reversal";
              return (
                <tr
                  key={p.id}
                  className={`${p.reversed ? "is-reversed" : ""} ${isReversal ? "is-reversal" : ""}`}
                >
                  <td>
                    {describe(p)}
                    {p.detail?.note && <div>{p.detail.note}</div>}
                  </td>
                  <td>{p.detail?.actor || "—"}</td>
                  <td>{(p.posted_at || "").slice(0, 10) || "—"}</td>
                  <td className="num">{signedCurrency(p.amount)}</td>
                  <td className="num">
                    {!readOnly && !isReversal && !p.reversed && (
                      <button
                        className="posting-reverse-btn"
                        onClick={() => reverse(p)}
                        disabled={reversingId === p.id}
                      >
                        {reversingId === p.id ? "Reversing…" : "Reverse"}
                      </button>
                    )}
                    {p.reversed && <span>reversed</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!readOnly && notPostable && (
        <p className="posting-not-postable">{money.postable_reason}</p>
      )}

      {!readOnly && (
        <div className="detail-actions" style={{ marginTop: 12 }}>
          <button
            className="btn btn-secondary btn-sm"
            disabled={postingsDisabled}
            title={notPostable ? money.postable_reason : undefined}
            onClick={(e) => {
              e.stopPropagation();
              setShowWriteoff(true);
            }}
          >
            Write off…
          </button>
          <button
            className="btn btn-secondary btn-sm"
            disabled={postingsDisabled}
            title={notPostable ? money.postable_reason : undefined}
            onClick={(e) => {
              e.stopPropagation();
              setShowPayment(true);
            }}
          >
            Record payment…
          </button>
        </div>
      )}

      {showWriteoff && (
        <WriteoffModal
          practiceId={practiceId}
          claim={claim}
          onClose={() => setShowWriteoff(false)}
          onPosted={applyResponse}
        />
      )}
      {showPayment && (
        <PatientPaymentModal
          practiceId={practiceId}
          claim={claim}
          onClose={() => setShowPayment(false)}
          onPosted={applyResponse}
        />
      )}
    </div>
  );
}
